import React, { memo, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';

import ContentHeader from 'modules/views/shared/Wizard/ContentHeader';
import Button from 'modules/views/shared/Button';
import OtpInput from 'modules/views/shared/OtpInput';
import WithBranding from 'modules/views/shared/WithBranding';
import FullscreenLoader from 'modules/views/shared/FullscreenLoader';

import { useVerifyOtp } from './hooks';
import { useStyles } from './styles';
import { ROUTES } from 'constants/routes';

/** The backend issues a 5-character alphanumeric one-time password. */
const OTP_LENGTH = 5;

interface IVerifyOtpLocationState {
  email?: string;
  mobilePhoneNumber?: string;
}

const VerifyOtp = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  const { email, mobilePhoneNumber } = (location.state || {}) as IVerifyOtpLocationState;
  const destination = mobilePhoneNumber || email || '';

  const { isLoading, isResuming, verifyOtp } = useVerifyOtp({
    onFailure: () => setOtpError(t('verifyOtp.errors.invalidOtp')),
  });

  // The email/mobile the OTP was sent to travels in router state, which does not survive a
  // reload. Without it there is nothing to verify against, so start over.
  useEffect(() => {
    if (!destination) {
      navigate(ROUTES.FORGOT_PASSWORD.path, { replace: true });
    }
  }, [destination, navigate]);

  // `OtpInput` re-runs its completion effect whenever these callbacks change identity,
  // so they have to be stable.
  const handleOtpChange = useCallback((value: string) => {
    setOtp(value);
    setOtpError('');
  }, []);

  const handleOtpComplete = useCallback((value: string) => setOtp(value), []);

  const handleSubmit = () => {
    if (otp.length < OTP_LENGTH) {
      setOtpError(t('verifyOtp.errors.otpRequired'));
      return;
    }
    verifyOtp(otp, email, mobilePhoneNumber);
  };

  // Render nothing while the effect above redirects, rather than flashing a form that
  // has no destination to verify against.
  if (!destination) {
    return null;
  }

  const Content = (
    <>
      <ContentHeader title={t('verifyOtp.title')} />
      <p className={classes.subtitle}>
        <Trans
          i18nKey="verifyOtp.subtitle"
          values={{ destination }}
          components={{ bold: <span className={classes.destination} /> }}
        />
      </p>

      <div className={classes.otpParent} onKeyDown={e => e.key === 'Enter' && handleSubmit()}>
        <OtpInput
          length={OTP_LENGTH}
          allowCharacters={true}
          autoFocus={true}
          disabled={isLoading}
          error={otpError}
          inputsParentClass={classes.otpInputs}
          onChange={handleOtpChange}
          onComplete={handleOtpComplete}
        />
      </div>

      <div className={classes.buttonParent}>
        <Button
          label={t('verifyOtp.buttons.verifyAccount')}
          hasEndArrow={true}
          isLoading={isLoading}
          disabled={otp.length < OTP_LENGTH}
          styleClass={classes.button}
          onClick={handleSubmit}
        />
      </div>
    </>
  );

  return (
    <>
      <WithBranding Content={Content} contentContainerStyleClass={classes.contentContainer} />
      {/* Only the CH-4652 resume earns an overlay — an ordinary verification is fast enough that
          the button spinner alone is the right amount of feedback. */}
      {isResuming && <FullscreenLoader transparent={true} />}
    </>
  );
};

export default memo(VerifyOtp);
